import axios from 'axios';
import API_CONFIG from '../config/api';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests automatically
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOKEN_REFRESH}`,
            { refresh: refreshToken }
          );
          localStorage.setItem('token', response.data.access);
          // Retry the original request
          return apiClient.request(error.config);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to get current user ID from token or API
const getCurrentUserId = async () => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.CURRENT_USER);
    return response.data.id;
  } catch (error) {
    console.error('Failed to get current user:', error);
    throw new Error('Unable to identify current user');
  }
};

// API methods
export const authAPI = {
  login: (username, password) =>
    apiClient.post(API_CONFIG.ENDPOINTS.TOKEN, { username, password }),
  
  getCurrentUser: () =>
    apiClient.get(API_CONFIG.ENDPOINTS.CURRENT_USER),
    
  refreshToken: (refreshToken) =>
    apiClient.post(API_CONFIG.ENDPOINTS.TOKEN_REFRESH, { refresh: refreshToken }),
};

export const userAPI = {
  getUsers: () => apiClient.get(API_CONFIG.ENDPOINTS.USERS),
  getUser: (id) => apiClient.get(`${API_CONFIG.ENDPOINTS.USERS}${id}/`),
  createUser: (userData) => apiClient.post(API_CONFIG.ENDPOINTS.USERS, userData),
  updateUser: (id, userData) => apiClient.put(`${API_CONFIG.ENDPOINTS.USERS}${id}/`, userData),
  deleteUser: (id) => apiClient.delete(`${API_CONFIG.ENDPOINTS.USERS}${id}/`),
};

export const taskAPI = {
  getTasks: () => apiClient.get(API_CONFIG.ENDPOINTS.TASKS),
  getTask: (id) => apiClient.get(`${API_CONFIG.ENDPOINTS.TASKS}${id}/`),
  createTask: (taskData) => apiClient.post(API_CONFIG.ENDPOINTS.TASKS, taskData),
  updateTask: (id, taskData) => apiClient.put(`${API_CONFIG.ENDPOINTS.TASKS}${id}/`, taskData),
  deleteTask: (id) => apiClient.delete(`${API_CONFIG.ENDPOINTS.TASKS}${id}/`),
  
  // Comment methods
  getTaskComments: (taskId) => apiClient.get(`${API_CONFIG.ENDPOINTS.TASKS}${taskId}/comments/`),
  
  // ✅ FIXED: Now includes author_id in the payload
  addTaskComment: async (taskId, commentData) => {
    const userId = await getCurrentUserId();
    const payload = {
      text: commentData.text,
      author_id: userId
    };
    return apiClient.post(`${API_CONFIG.ENDPOINTS.TASKS}${taskId}/comments/`, payload);
  },
  
  updateTaskComment: (taskId, commentId, commentData) => 
    apiClient.put(`${API_CONFIG.ENDPOINTS.TASKS}${taskId}/comments/${commentId}/`, commentData),
  
  deleteTaskComment: (taskId, commentId) => 
    apiClient.delete(`${API_CONFIG.ENDPOINTS.TASKS}${taskId}/comments/${commentId}/`),
};

export const workspaceAPI = {
  getWorkspaces: () => apiClient.get(API_CONFIG.ENDPOINTS.WORKSPACES),
  getWorkspace: (id) => apiClient.get(`${API_CONFIG.ENDPOINTS.WORKSPACES}${id}/`),
  createWorkspace: (workspaceData) => apiClient.post(API_CONFIG.ENDPOINTS.WORKSPACES, workspaceData),
  updateWorkspace: (id, workspaceData) => apiClient.put(`${API_CONFIG.ENDPOINTS.WORKSPACES}${id}/`, workspaceData),
  deleteWorkspace: (id) => apiClient.delete(`${API_CONFIG.ENDPOINTS.WORKSPACES}${id}/`),
};

// Chat API methods - Updated to match required structure
// Chat API methods - Updated to use proper API_CONFIG endpoints
export const chatAPI = {
  // Primary methods using API_CONFIG endpoints
  getChatRooms: () => apiClient.get(API_CONFIG.ENDPOINTS.CHATS || '/chats/'),
  
  getRoomMessages: (roomId) => {
    const endpoint = API_CONFIG.ENDPOINTS.CHAT_MESSAGES 
      ? API_CONFIG.ENDPOINTS.CHAT_MESSAGES.replace('{chatId}', roomId)
      : `/chats/${roomId}/messages/`;
    return apiClient.get(endpoint);
  },
  
  createChatRoom: (data) => apiClient.post(API_CONFIG.ENDPOINTS.CHATS || '/chats/', data),
  
  sendMessage: (data) => {
    const endpoint = API_CONFIG.ENDPOINTS.MESSAGES || '/messages/';
    return apiClient.post(endpoint, data);
  },
  
  deleteMessage: (messageId) => {
    const endpoint = API_CONFIG.ENDPOINTS.MESSAGES || '/messages/';
    return apiClient.delete(`${endpoint}${messageId}/`);
  },
  
  getOrCreateGroupChat: () => {
    const endpoint = API_CONFIG.ENDPOINTS.CHATS || '/chats/';
    return apiClient.post(`${endpoint}group/`);
  },
  
  // Fallback methods with proper endpoint handling
  getChats: () => apiClient.get(API_CONFIG.ENDPOINTS.CHATS || '/chats/'),
  getChat: (chatId) => {
    const endpoint = API_CONFIG.ENDPOINTS.CHATS || '/chats/';
    return apiClient.get(`${endpoint}${chatId}/`);
  },
  createChat: (chatData) => apiClient.post(API_CONFIG.ENDPOINTS.CHATS || '/chats/', chatData),
  
  getChatMessages: (chatId) => {
    const endpoint = API_CONFIG.ENDPOINTS.CHAT_MESSAGES 
      ? API_CONFIG.ENDPOINTS.CHAT_MESSAGES.replace('{chatId}', chatId)
      : `/chats/${chatId}/messages/`;
    return apiClient.get(endpoint);
  },
  
  // Enhanced sendMessage with user ID handling
  sendMessageWithUser: async (chatId, messageData) => {
    const userId = await getCurrentUserId();
    const endpoint = API_CONFIG.ENDPOINTS.CHAT_MESSAGES 
      ? API_CONFIG.ENDPOINTS.CHAT_MESSAGES.replace('{chatId}', chatId)
      : `/chats/${chatId}/messages/`;
    const payload = {
      text: messageData.message || messageData.text,
      sender_id: userId,
      chat_id: chatId,
      ...messageData
    };
    return apiClient.post(endpoint, payload);
  },
  
  sendGroupMessage: async (messageData) => {
    const userId = await getCurrentUserId();
    const endpoint = API_CONFIG.ENDPOINTS.MESSAGES || '/messages/';
    const payload = {
      text: messageData.message || messageData.text,
      sender_id: userId,
      chat_type: 'group',
      ...messageData
    };
    return apiClient.post(endpoint, payload);
  },
  
  updateMessage: (messageId, messageData) => {
    const endpoint = API_CONFIG.ENDPOINTS.MESSAGES || '/messages/';
    return apiClient.put(`${endpoint}${messageId}/`, messageData);
  },
  
  getMessages: (params = {}) => {
    const endpoint = API_CONFIG.ENDPOINTS.MESSAGES || '/messages/';
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`${endpoint}${queryString ? '?' + queryString : ''}`);
  },
};

export default apiClient;